import { useEffect, useState } from 'react';
import { CanvasElement as CanvasElementType, MapSettings, PosterSettings, WorkspaceBackground, PosterTextData, MapIcon } from '@/types/canvas';
import { CanvasElement } from './CanvasElement';
import { SizeIndicators } from './SizeIndicators';
// import { RotationIcon } from './RotationIcon';
import { calculateMockupTransform, calculateDynamicShadows } from '@/utils/mockupPresets';
import { FrameWrapper } from './FrameWrapper';
import { PosterRenderer } from './PosterRenderer';

interface CanvasProps {
  elements: CanvasElementType[];
  mapIcons?: MapIcon[];
  selectedId: string | null;
  selectedIconId?: string | null;
  selectedMapIconId?: string | null;
  selectedIconColor?: string;
  canvasWidth: number;
  canvasHeight: number;
  mapSettings: MapSettings;
  posterSettings: PosterSettings;
  workspaceBackground: WorkspaceBackground;
  onSelect: (id: string | null) => void;
  onUpdateElement: (id: string, updates: Partial<CanvasElementType>) => void;
  onMapChange?: (lat: number, lng: number, zoom: number) => void;
  onMapClick?: (lat: number, lng: number) => void;
  onIconClick?: (iconId: string) => void;
  onIconUpdate?: (iconId: string, lat: number, lng: number) => void;
  onIconSelectCancel?: () => void;
  onMapInstanceReady?: (mapInstance: any) => void;
  textData: PosterTextData;
  onToggleOrientation?: () => void;
}

export function Canvas({
  elements,
  mapIcons = [],
  selectedId,
  selectedIconId = null,
  selectedMapIconId = null,
  selectedIconColor = '#000000',
  canvasWidth,
  canvasHeight,
  mapSettings,
  posterSettings,
  workspaceBackground,
  onSelect,
  onUpdateElement,
  onMapChange,
  onMapClick,
  onIconClick,
  onIconUpdate,
  onIconSelectCancel,
  onMapInstanceReady,
  textData,
  // onToggleOrientation,
}: CanvasProps) {
  // Automaticky aplikovat mockup transformace když je aktivní living-room pozadí
  useEffect(() => {
    if (workspaceBackground === 'living-room') {
      // const mockupTransform = calculateMockupTransform(
      calculateMockupTransform(
        posterSettings.orientation
      );

      // Aktualizovat poster settings pouze pokud se změnila orientace nebo velikost
      // a pokud hodnoty nejsou již nastaveny manuálně
      const needsUpdate =
        !posterSettings.offsetX &&
        !posterSettings.offsetY &&
        !posterSettings.scale;

      if (needsUpdate && onMapChange) {
        // Poznámka: onMapChange je pro mapu, potřebovali bychom onPosterChange
        // Prozatím necháme auto-aplikaci přes inline style
      }
    }
  }, [workspaceBackground, posterSettings.orientation, canvasWidth, canvasHeight]);

  // Pokud je aktivní mockup, použít automatické hodnoty
  const getMockupAwareSettings = () => {
    if (workspaceBackground === 'living-room') {
      const autoTransform = calculateMockupTransform(
        posterSettings.orientation
      );

      return {
        scale: posterSettings.scale || autoTransform.scale || 1,
        offsetX: posterSettings.offsetX !== undefined ? posterSettings.offsetX : autoTransform.offsetX || 0,
        offsetY: posterSettings.offsetY !== undefined ? posterSettings.offsetY : autoTransform.offsetY || 0,
        perspective: posterSettings.perspective !== undefined ? posterSettings.perspective : autoTransform.perspective,
        rotateX: posterSettings.rotateX !== undefined ? posterSettings.rotateX : autoTransform.rotateX || 0,
        rotateY: posterSettings.rotateY !== undefined ? posterSettings.rotateY : autoTransform.rotateY || 0,
        rotateZ: posterSettings.rotateZ !== undefined ? posterSettings.rotateZ : autoTransform.rotateZ || 0,
      };
    }

    // Bez mockupu použít standardní hodnoty
    return {
      scale: posterSettings.scale || 1,
      offsetX: posterSettings.offsetX || 0,
      offsetY: posterSettings.offsetY || 0,
      perspective: posterSettings.perspective,
      rotateX: posterSettings.rotateX || 0,
      rotateY: posterSettings.rotateY || 0,
      rotateZ: posterSettings.rotateZ || 0,
    };
  };

  const { scale: posterScale, offsetX: posterOffsetX, offsetY: posterOffsetY, perspective, rotateX, rotateY, rotateZ } = getMockupAwareSettings();

  // Vypočítat dynamické stíny pro mockup
  const shadowEffects = workspaceBackground === 'living-room'
    ? calculateDynamicShadows(
      posterOffsetX,
      posterOffsetY,
      rotateX,
      rotateY,
      rotateZ,
      posterScale,
      canvasWidth,
      canvasHeight
    )
    : null;

  const getPosterTransform = () => {
    const transforms = [];

    if (posterOffsetX || posterOffsetY) {
      transforms.push(`translate(${posterOffsetX}px, ${posterOffsetY}px)`);
    }

    if (rotateX || rotateY || rotateZ) {
      if (rotateX) transforms.push(`rotateX(${rotateX}deg)`);
      if (rotateY) transforms.push(`rotateY(${rotateY}deg)`);
      if (rotateZ) transforms.push(`rotateZ(${rotateZ}deg)`);
    }

    if (posterScale !== 1) {
      transforms.push(`scale(${posterScale})`);
    }

    return transforms.join(' ');
  };

  // Funkce pro výpočet dostupné velikosti podle workingtable containeru
  const calculateAvailableSize = () => {
    if (typeof window === 'undefined') return { width: canvasWidth, height: canvasHeight };

    const workingTable = document.getElementById('workingtable');
    if (!workingTable) return { width: canvasWidth, height: canvasHeight };

    const containerRect = workingTable.getBoundingClientRect();

    // Rezervovat prostor pro rozměrové ukazatele
    const indicatorSpace = 80; // Prostor pro ukazatele (40px na každou stranu)
    const margin = 20; // Obecný margin

    const availableWidth = Math.max(300, containerRect.width - indicatorSpace - margin);
    const availableHeight = Math.max(300, containerRect.height - indicatorSpace - margin);

    // Zachovat poměr stran původního plakátu, ale přizpůsobit dostupnému prostoru
    const originalRatio = canvasWidth / canvasHeight;
    let newWidth = availableWidth;
    let newHeight = availableHeight;

    if (newWidth / newHeight > originalRatio) {
      // Dostupná šířka je příliš velká, omezte ji podle výšky
      newWidth = newHeight * originalRatio;
    } else {
      // Dostupná výška je příliš velká, omezte ji podle šířky
      newHeight = newWidth / originalRatio;
    }

    // Aplikovat zoom
    const zoomedWidth = newWidth * zoomLevel;
    const zoomedHeight = newHeight * zoomLevel;

    return {
      width: Math.floor(zoomedWidth),
      height: Math.floor(zoomedHeight)
    };
  };

  const [availableSize, setAvailableSize] = useState({ width: canvasWidth, height: canvasHeight });
  const [zoomLevel, setZoomLevel] = useState(1); // Zoom pro náhled

  // Funkce pro ovládání zoomu
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 8)); // Max zoom 8x
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5)); // Min zoom 0.5x
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
  };

  // Keyboard shortcuts pro zoom
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case '=':
          case '+':
            e.preventDefault();
            handleZoomIn();
            break;
          case '-':
            e.preventDefault();
            handleZoomOut();
            break;
          case '0':
            e.preventDefault();
            handleZoomReset();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Aktualizace dostupné velikosti při změně velikosti okna
  useEffect(() => {
    const handleResize = () => {
      const newSize = calculateAvailableSize();
      setAvailableSize(newSize);
    };

    // Počáteční výpočet po mount
    const timer = setTimeout(() => {
      handleResize();
    }, 100);

    // Přidat listener pro změny velikosti okna
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [canvasWidth, canvasHeight, zoomLevel]);

  return (
    <div
      id="canvas-main-container"
      className="w-full h-full flex items-center justify-center relative"
      style={{
        perspective: perspective ? `${perspective}px` : undefined,
        minHeight: '400px',
      }}
    >
      {/* Wrapper: na desktopu poster + zoom vedle; na mobilu poster nahoře, zoom pod mapou */}
      <div className="relative inline-flex flex-col lg:flex-row items-center lg:items-start gap-4 lg:gap-2">
        <div
          id="canvas-poster-container"
          className="flex-shrink-0 relative"
        style={{
          transform: getPosterTransform(),
          transformOrigin: 'center',
          transformStyle: 'preserve-3d',
        }}
      >
        <FrameWrapper
          frame={posterSettings.frame}
          frameColor={posterSettings.frameColor}
          posterWidth={availableSize.width}
          posterHeight={availableSize.height}
        >
          <div
            id="canvas-content-container"
            className="relative bg-white"
            style={{
              width: `${availableSize.width}px`,
              height: `${availableSize.height}px`,
              overflow: 'hidden',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                onSelect(null);
              }
            }}
          >
            {/* Dynamické stíny pro mockup */}
            {shadowEffects && (
              <>
                <div id="canvas-wall-shadow" style={shadowEffects.wallShadow} />
                <div id="canvas-ground-shadow" style={shadowEffects.groundShadow} />
              </>
            )}

            {shadowEffects && <div id="canvas-inner-shadow" style={shadowEffects.innerShadow} />}

            {workspaceBackground === 'css-shadow' && (
              <div
                id="canvas-organic-shadows"
                style={{
                  position: 'absolute',
                  top: '-20px',
                  left: '-25px',
                  right: '-25px',
                  bottom: '-20px',
                  background: `
                    radial-gradient(ellipse 80px 50px at 25% 15%, rgba(0, 0, 0, 0.06) 0%, transparent 50%),
                    radial-gradient(ellipse 60px 40px at 75% 25%, rgba(0, 0, 0, 0.04) 0%, transparent 40%),
                    radial-gradient(ellipse 100px 60px at 50% 80%, rgba(0, 0, 0, 0.03) 0%, transparent 60%)
                  `,
                  pointerEvents: 'none',
                  zIndex: -1,
                  borderRadius: '12px',
                }}
              />
            )}

            <PosterRenderer
              width={availableSize.width}
              height={availableSize.height}
              mapSettings={mapSettings}
              posterSettings={posterSettings}
              textData={textData}
              mapIcons={mapIcons}
              selectedIconId={selectedIconId}
              selectedMapIconId={selectedMapIconId}
              selectedIconColor={selectedIconColor}
              onMapChange={onMapChange}
              onMapClick={(lat, lng) => {
                if (onMapClick) {
                  onMapClick(lat, lng);
                }
                onSelect(null);
              }}
              onIconClick={onIconClick}
              onIconUpdate={onIconUpdate}
              onIconSelectCancel={onIconSelectCancel}
              onMapInstanceReady={onMapInstanceReady}
            />

            <SizeIndicators
              width={availableSize.width}
              height={availableSize.height}
              posterSize={posterSettings.size}
              orientation={posterSettings.orientation}
            />

            {elements
              .filter((el) => el.type === 'icon')
              .map((element) => (
                <CanvasElement
                  key={element.id}
                  element={element}
                  isSelected={selectedId === element.id}
                  onSelect={() => onSelect(element.id)}
                  onUpdate={(updates) => onUpdateElement(element.id, updates)}
                />
              ))}
          </div>
        </FrameWrapper>
        </div>
        {/* Zoom ovládání: na desktopu vedle pravého horního rohu mapy, na mobilu pod mapou */}
        <div className="relative lg:absolute lg:left-full lg:top-0 lg:ml-2 mt-4 lg:mt-0 flex flex-row lg:flex-col gap-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg">
          <button
            onClick={handleZoomIn}
            className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-sm font-bold"
            title="Zoom In"
          >
            +
          </button>
          <button
            onClick={handleZoomOut}
            className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-sm font-bold"
            title="Zoom Out"
          >
            −
          </button>
          <button
            onClick={handleZoomReset}
            className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-xs font-bold"
            title="Reset Zoom"
          >
            1:1
          </button>
          <div className="text-xs text-center text-gray-600 font-mono">
            {Math.round(zoomLevel * 100)}%
          </div>
        </div>
      </div>
    </div>
  );
}

